"use client"

import { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import { FiX, FiUsers, FiPlus, FiTrash2, FiLayers, FiUser, FiFolder, FiChevronRight } from "react-icons/fi"
import axios from "axios"
import Header from "./Header"
import Sidebar from "./Sidebar"
import Select from "react-select"
import Notification from "./Notification"

function GroupDetails() {
  const { group_id } = useParams()
  const [groupName, setGroupName] = useState("")
  const [users, setUsers] = useState([])
  const [allUsers, setAllUsers] = useState([])
  const [selectedUsers, setSelectedUsers] = useState([])
  const [notification, setNotification] = useState(null)
  const [sidebarOpen, setSidebarOpen] = useState(() => (typeof window !== "undefined" ? window.innerWidth >= 1024 : true))
  const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState(null)
  const [projects, setProjects] = useState([])
  const [selectedProjects, setSelectedProjects] = useState([])
  const [groupProjects, setGroupProjects] = useState([])
  const [isAddingUsers, setIsAddingUsers] = useState(false)
  const [isAddingProjects, setIsAddingProjects] = useState(false)
  const [isDeleteProjectModalOpen, setIsDeleteProjectModalOpen] = useState(false)
  const [projectToDelete, setProjectToDelete] = useState(null)

  // Custom styles for react-select to match the design system
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      border: "1px solid #d1d5db",
      borderRadius: "0.5rem",
      padding: "0.25rem",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(59, 130, 246, 0.5)" : "none",
      borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
      "&:hover": {
        borderColor: "#9ca3af",
      },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      position: "absolute",
      top: "100%",
      left: 0,
      right: 0,
      backgroundColor: "white",
      border: "1px solid #d1d5db",
      borderRadius: "0.5rem",
      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
      maxHeight: "200px",
      overflowY: "auto",
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "#3b82f6" : state.isFocused ? "#eff6ff" : "white",
      color: state.isSelected ? "white" : "#374151",
      padding: "0.75rem",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: state.isSelected ? "#3b82f6" : "#eff6ff",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#9ca3af",
    }),
  }

  // Fetch group details
  useEffect(() => {
    const fetchGroupDetails = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/groups/${group_id}`)
        setGroupName(response.data.group_name)
      } catch (error) {
        console.error("Error fetching group details:", error)
        setNotification({
          type: "error",
          message: "Failed to load group details. Please try again.",
        })
      }
    }
    fetchGroupDetails()
  }, [group_id])

  // Fetch users in the group
  useEffect(() => {
    const fetchGroupUsers = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/groups/${group_id}/users`)
        setUsers(response.data)
      } catch (error) {
        console.error("Error fetching group users:", error)
        setNotification({
          type: "error",
          message: "Failed to load group users. Please try again.",
        })
      }
    }
    fetchGroupUsers()
  }, [group_id])

  // Fetch all users for the dropdown
  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user"))
        if (user && user.company_id) {
          const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/company/${user.company_id}`)
          setAllUsers(response.data)
        } else {
          setAllUsers([])
          setNotification({
            type: "error",
            message: "User information not found. Please log in again.",
          })
        }
      } catch (error) {
        setAllUsers([])
        setNotification({
          type: "error",
          message: "Failed to load users. Please try again.",
        })
      }
    }
    fetchAllUsers()
  }, [])

  // Fetch all projects for the dropdown
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user"))
        const companyId = user?.company_id || "COMP-73655"
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/projects/company/${companyId}`)
        const projectOptions = (response.data || [])
          .filter((project) => project.project_id)
          .map((project) => ({
            value: project.project_id,
            label: project.project_id,
          }))
        setProjects(projectOptions)
      } catch (error) {
        console.error("Error fetching projects:", error)
        setNotification({
          type: "error",
          message: "Failed to load projects. Please try again.",
        })
      }
    }
    fetchProjects()
  }, [])

  // Fetch projects in the group
  useEffect(() => {
    const fetchGroupProjects = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/projects/group/${group_id}/projects`,
        )
        setGroupProjects(response.data)
      } catch (error) {
        console.error("Error fetching group projects:", error)
        setNotification({
          type: "error",
          message: "Failed to load group projects. Please try again.",
        })
      }
    }
    fetchGroupProjects()
  }, [group_id])

  // Handle adding multiple users to the group
  const handleAddUsers = async (e) => {
    e.preventDefault()

    if (selectedUsers.length === 0) {
      setNotification({
        type: "error",
        message: "Please select at least one user to add.",
      })
      return
    }

    setIsAddingUsers(true)

    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/groups/${group_id}/users`, selectedUsers)

      setNotification({
        type: "success",
        message: "Users added to group successfully!",
      })

      // Refresh the user list
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/groups/${group_id}/users`)
      setUsers(response.data)
      setSelectedUsers([])
    } catch (error) {
      console.error("Error adding users to group:", error)
      setNotification({
        type: "error",
        message: "Failed to add users to group. Please try again.",
      })
    } finally {
      setIsAddingUsers(false)
    }
  }

  // Handle adding multiple projects to the group
  const handleAddProjects = async (e) => {
    e.preventDefault()

    if (selectedProjects.length === 0) {
      setNotification({
        type: "error",
        message: "Please select at least one project to add.",
      })
      return
    }

    setIsAddingProjects(true)

    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/groups/${group_id}/projects`, selectedProjects, {
        headers: { "Content-Type": "application/json" },
      })

      setNotification({
        type: "success",
        message: "Projects added to group successfully!",
      })

      // Refresh the project list
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/projects/group/${group_id}/projects`)
      setGroupProjects(response.data)
      setSelectedProjects([])
    } catch (error) {
      console.error("Error adding projects to group:", error)
      setNotification({
        type: "error",
        message: "Failed to add projects to group. Please try again.",
      })
    } finally {
      setIsAddingProjects(false)
    }
  }

  // Handle removing a user from the selected list
  const handleRemoveSelectedUser = (userId) => {
    setSelectedUsers((prev) => prev.filter((id) => id !== userId))
  }

  // Handle removing a project from the selected list
  const handleRemoveSelectedProject = (projectId) => {
    setSelectedProjects((prev) => prev.filter((id) => id !== projectId))
  }

  // Handle removing a user from the group
  const handleRemoveUserFromGroup = async (userId) => {
    setUserToDelete(userId)
    setIsDeleteUserModalOpen(true)
  }

  // Handle removing a project from the group
  const handleRemoveProjectFromGroup = async (projectId) => {
    setProjectToDelete(projectId)
    setIsDeleteProjectModalOpen(true)
  }

  // Confirm delete user from group
  const confirmDeleteUserFromGroup = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/groups/${group_id}/users/${userToDelete}`)
      setNotification({
        type: "success",
        message: "User removed from group successfully!",
      })

      // Refresh the user list
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/groups/${group_id}/users`)
      setUsers(response.data)
    } catch (error) {
      console.error("Error removing user from group:", error)
      setNotification({
        type: "error",
        message: "Failed to remove user from group. Please try again.",
      })
    } finally {
      setIsDeleteUserModalOpen(false)
      setUserToDelete(null)
    }
  }

  // Confirm delete project from group
  const confirmDeleteProjectFromGroup = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/groups/${group_id}/projects/${projectToDelete}`)
      setNotification({
        type: "success",
        message: "Project removed from group successfully!",
      })

      // Refresh the project list
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/projects/group/${group_id}/projects`)
      setGroupProjects(response.data)
    } catch (error) {
      console.error("Error removing project from group:", error)
      setNotification({
        type: "error",
        message: "Failed to remove project from group. Please try again.",
      })
    } finally {
      setIsDeleteProjectModalOpen(false)
      setProjectToDelete(null)
    }
  }

  // Cancel delete user
  const cancelDeleteUser = () => {
    setIsDeleteUserModalOpen(false)
    setUserToDelete(null)
  }

  // Cancel delete project
  const cancelDeleteProject = () => {
    setIsDeleteProjectModalOpen(false)
    setProjectToDelete(null)
  }

  // Close notification
  const closeNotification = () => {
    setNotification(null)
  }

  // Get available users (not already in group)
  const availableUsers = allUsers.filter(
    (user) => !users.some((groupUser) => groupUser.id_user === user.id_user) && !selectedUsers.includes(user.id_user),
  )

  // Get available projects (not already in group)
  const availableProjects = projects.filter(
    (project) => !groupProjects.includes(project.value) && !selectedProjects.includes(project.value),
  )

  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <Header toggleSidebar={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} />

      <div className="flex flex-1 bg-gray-100 min-w-0 max-w-full overflow-x-hidden">
        {/* Sidebar */}
        <Sidebar isOpen={sidebarOpen} toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />

        {/* Main Content */}
        <div className={`flex-1 min-w-0 max-w-full overflow-x-hidden p-3 sm:p-6 transition-all duration-300 ${sidebarOpen ? "lg:ml-64" : "lg:ml-20"} ml-0`}>
          <div className="pt-16 sm:pt-20 pb-6">
            {/* Page Header */}
            <div className="mb-6 sm:mb-8">
              <div className="flex items-center text-sm text-gray-500 mb-2">
                <span>Groups</span>
                <FiChevronRight className="w-4 h-4 mx-2" />
                <span className="text-gray-900 font-medium">{groupName}</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Group Details: {groupName}</h1>
              <p className="text-gray-600 text-sm sm:text-base">Manage users and projects for this group</p>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 sm:gap-8">
              {/* Users Section */}
              <div className="space-y-6">
                {/* Current Users */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 sm:px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center">
                      <div className="bg-blue-100 p-2 rounded-lg mr-3">
                        <FiUsers className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                      </div>
                      <div>
                        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Group Members</h2>
                        <p className="text-xs sm:text-sm text-gray-600">
                          {users.length} {users.length === 1 ? "member" : "members"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 sm:p-6">
                    {users.length > 0 ? (
                      <div className="space-y-3">
                        {users.map((user) => (
                          <div
                            key={user.id_user}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex items-center">
                              <div className="bg-blue-100 p-2 rounded-full mr-3">
                                <FiUser className="w-4 h-4 text-blue-600" />
                              </div>
                              <div>
                                <div className="font-medium text-gray-900 text-sm sm:text-base">{user.full_name}</div>
                                <div className="text-xs sm:text-sm text-gray-500">@{user.username}</div>
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveUserFromGroup(user.id_user)}
                              className="text-red-600 hover:text-red-800 p-1 rounded-md hover:bg-red-50 transition-colors"
                              title="Remove user from group"
                            >
                              <FiTrash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="bg-gray-100 p-3 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                          <FiUsers className="w-6 h-6 text-gray-400" />
                        </div>
                        <p className="text-gray-500 text-sm">No members in this group yet</p>
                        <p className="text-gray-400 text-xs mt-1">Add users to get started</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Add Users */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-4 sm:px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center">
                      <div className="bg-green-100 p-2 rounded-lg mr-3">
                        <FiPlus className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Add Members</h3>
                        <p className="text-xs sm:text-sm text-gray-600">Select users to add to this group</p>
                      </div>
                    </div>
                  </div>

                  <form onSubmit={handleAddUsers} className="p-4 sm:p-6 space-y-4">
                    {/* Selected Users Display */}
                    {selectedUsers.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Selected Users ({selectedUsers.length})
                        </label>
                        <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border">
                          {selectedUsers.map((userId) => {
                            const user = allUsers.find((u) => u.id_user === userId)
                            return (
                              <div
                                key={userId}
                                className="flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm border border-blue-200"
                              >
                                <span className="mr-2">{user?.full_name}</span>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveSelectedUser(userId)}
                                  className="text-blue-600 hover:text-blue-800 transition-colors"
                                >
                                  <FiX className="w-3 h-3" />
                                </button>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}

                    {/* User Selection Dropdown */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Select Users to Add</label>
                      <Select
                        options={availableUsers.map((user) => ({
                          value: user.id_user,
                          label: `${user.full_name} (@${user.username})`,
                        }))}
                        onChange={(selectedOption) => {
                          if (selectedOption && !selectedUsers.includes(selectedOption.value)) {
                            setSelectedUsers((prev) => [...prev, selectedOption.value])
                          }
                        }}
                        placeholder="Choose a user to add..."
                        className="w-full"
                        classNamePrefix="react-select"
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="absolute"
                        isDisabled={availableUsers.length === 0}
                        noOptionsMessage={() => "No available users to add"}
                      />
                      {availableUsers.length === 0 && (
                        <p className="text-xs text-gray-500 mt-1">All users are already in this group</p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={isAddingUsers || selectedUsers.length === 0}
                      className={`w-full flex items-center justify-center py-3 px-4 rounded-lg font-medium transition-all duration-200 ${
                        isAddingUsers || selectedUsers.length === 0
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-green-600 text-white hover:bg-green-700 hover:shadow-md transform hover:scale-[1.02]"
                      }`}
                    >
                      {isAddingUsers ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                          Adding Users...
                        </>
                      ) : (
                        <>
                          <FiPlus className="w-4 h-4 mr-2" />
                          Add {selectedUsers.length > 0 ? `${selectedUsers.length} ` : ""}User
                          {selectedUsers.length !== 1 ? "s" : ""}
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>

              {/* Projects Section */}
              <div className="space-y-6">
                {/* Current Projects */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-purple-50 to-violet-50 px-4 sm:px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center">
                      <div className="bg-purple-100 p-2 rounded-lg mr-3">
                        <FiLayers className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                      </div>
                      <div>
                        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Group Projects</h2>
                        <p className="text-xs sm:text-sm text-gray-600">
                          {groupProjects.length} {groupProjects.length === 1 ? "project" : "projects"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 sm:p-6">
                    {groupProjects.length > 0 ? (
                      <div className="space-y-3">
                        {groupProjects.map((projectId, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex items-center">
                              <div className="bg-purple-100 p-2 rounded-full mr-3">
                                <FiFolder className="w-4 h-4 text-purple-600" />
                              </div>
                              <div>
                                <div className="font-medium text-gray-900 text-sm sm:text-base font-mono">
                                  {projectId}
                                </div>
                                <div className="text-xs sm:text-sm text-gray-500">Project ID</div>
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveProjectFromGroup(projectId)}
                              className="text-red-600 hover:text-red-800 p-1 rounded-md hover:bg-red-50 transition-colors"
                              title="Remove project from group"
                            >
                              <FiTrash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="bg-gray-100 p-3 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                          <FiLayers className="w-6 h-6 text-gray-400" />
                        </div>
                        <p className="text-gray-500 text-sm">No projects in this group yet</p>
                        <p className="text-gray-400 text-xs mt-1">Add projects to get started</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Add Projects */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-4 sm:px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center">
                      <div className="bg-orange-100 p-2 rounded-lg mr-3">
                        <FiPlus className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Add Projects</h3>
                        <p className="text-xs sm:text-sm text-gray-600">Select projects to add to this group</p>
                      </div>
                    </div>
                  </div>

                  <form onSubmit={handleAddProjects} className="p-4 sm:p-6 space-y-4">
                    {/* Selected Projects Display */}
                    {selectedProjects.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Selected Projects ({selectedProjects.length})
                        </label>
                        <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border">
                          {selectedProjects.map((projectId) => {
                            const project = projects.find((p) => p.value === projectId)
                            return (
                              <div
                                key={projectId}
                                className="flex items-center bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm border border-orange-200"
                              >
                                <span className="mr-2 font-mono">{project?.label}</span>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveSelectedProject(projectId)}
                                  className="text-orange-600 hover:text-orange-800 transition-colors"
                                >
                                  <FiX className="w-3 h-3" />
                                </button>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}

                    {/* Project Selection Dropdown */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Select Projects to Add</label>
                      <Select
                        options={availableProjects}
                        onChange={(selectedOption) => {
                          if (selectedOption && !selectedProjects.includes(selectedOption.value)) {
                            setSelectedProjects((prev) => [...prev, selectedOption.value])
                          }
                        }}
                        placeholder="Choose a project to add..."
                        className="w-full"
                        classNamePrefix="react-select"
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="absolute"
                        isDisabled={availableProjects.length === 0}
                        noOptionsMessage={() => "No available projects to add"}
                      />
                      {availableProjects.length === 0 && (
                        <p className="text-xs text-gray-500 mt-1">All projects are already in this group</p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={isAddingProjects || selectedProjects.length === 0}
                      className={`w-full flex items-center justify-center py-3 px-4 rounded-lg font-medium transition-all duration-200 ${
                        isAddingProjects || selectedProjects.length === 0
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-orange-600 text-white hover:bg-orange-700 hover:shadow-md transform hover:scale-[1.02]"
                      }`}
                    >
                      {isAddingProjects ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                          Adding Projects...
                        </>
                      ) : (
                        <>
                          <FiPlus className="w-4 h-4 mr-2" />
                          Add {selectedProjects.length > 0 ? `${selectedProjects.length} ` : ""}Project
                          {selectedProjects.length !== 1 ? "s" : ""}
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notification Component */}
      {notification && (
        <Notification type={notification.type} message={notification.message} onClose={closeNotification} />
      )}

      {/* Delete User Confirmation Modal */}
      {isDeleteUserModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-4 sm:p-6">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-4">
                <FiTrash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Remove User from Group</h3>
            </div>
            <p className="text-gray-600 mb-6 text-sm sm:text-base">
              Are you sure you want to remove this user from the group? This action cannot be undone.
            </p>
            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                type="button"
                onClick={cancelDeleteUser}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm sm:text-base"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteUserFromGroup}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm sm:text-base"
              >
                Remove User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Project Confirmation Modal */}
      {isDeleteProjectModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-4 sm:p-6">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-4">
                <FiTrash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Remove Project from Group</h3>
            </div>
            <p className="text-gray-600 mb-6 text-sm sm:text-base">
              Are you sure you want to remove this project from the group? This action cannot be undone.
            </p>
            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                type="button"
                onClick={cancelDeleteProject}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm sm:text-base"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteProjectFromGroup}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm sm:text-base"
              >
                Remove Project
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GroupDetails
